import type { DashboardLayoutResponse, DashboardUser } from "@repo/shared/types";
import { PageHeader } from "@/components/page-header";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./dashboard-header.i18n";
import { useDashboardFormat } from "./use-dashboard-format";

/** The select's value for the Performance Dashboard; any other value is a user id. */
export const PERFORMANCE_OPTION = "performance";

type DashboardHeaderProps = {
  layout: DashboardLayoutResponse | null;
  isAdmin: boolean;
  currentUserId: string;
  /** The select's value: PERFORMANCE_OPTION or the id of the user shown. */
  selected: string;
  tenantUsers: DashboardUser[];
  onSelect: (value: string) => void;
};

export function DashboardHeader({
  layout,
  isAdmin,
  currentUserId,
  selected,
  tenantUsers,
  onSelect,
}: DashboardHeaderProps) {
  const t = useT(T);
  const { count } = useDashboardFormat();
  const performance = selected === PERFORMANCE_OPTION;

  const clients = (n: number) => (n === 1 ? t.clientCountOne : t.clientCount(count(n)));
  const users = (n: number) => (n === 1 ? t.userCountOne : t.userCount(count(n)));
  const summary = layout
    ? layout.summary.userCount !== null
      ? `${t.tenantWide} · ${users(layout.summary.userCount)} · ${clients(layout.summary.clientCount)}`
      : `${layout.summary.name ?? ""} · ${clients(layout.summary.clientCount)}`
    : undefined;

  // Admins switch dashboards; everyone else only has their own, so no select.
  const others = tenantUsers.filter((u) => u.id !== currentUserId);

  return (
    <PageHeader
      title={performance ? t.performanceDashboard : t.userDashboard}
      description={summary}
      actions={
        isAdmin && (
          <div className="space-y-1">
            <Label htmlFor="dashboard-select">{t.dashboardSelect}</Label>
            <Select value={selected} onValueChange={onSelect}>
              <SelectTrigger id="dashboard-select" className="w-[230px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t.tenantDashboard}</SelectLabel>
                  <SelectItem value={PERFORMANCE_OPTION}>{t.performanceDashboard}</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>{t.userDashboards}</SelectLabel>
                  <SelectItem value={currentUserId}>{t.me}</SelectItem>
                  {others.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )
      }
    />
  );
}
